/**
 * TrameLivretQR.jsx — QR livret ciblé sur un chapitre.
 *
 * Génère un QR code + lien copiable qui pointe vers un chapitre PRÉCIS
 * du livret interactif correspondant à la trame (Plateau ou Circulation).
 *
 * Deux modes :
 *   - AUTO : le chapitre proposé est déduit automatiquement de la phase
 *            en cours (via `phaseToChapterMap[trameKey][phaseId]`).
 *   - MANUEL : l'enseignant peut choisir explicitement un chapitre parmi
 *              ceux disponibles dans le livret via un `<select>`.
 *
 * Objectif pédagogique : à la fin d'une phase de trame (par ex. P2 slalom),
 * l'enseignant peut montrer un QR qui ramène l'élève directement au
 * chapitre correspondant du livret pour cocher/documenter la manœuvre.
 *
 * URL cible : /livret-<trameKey>?chapter=chXX
 * (le livret gère déjà `?chapter=id` en deep-link ; cf. LivretPlateauBookPublic)
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, BookOpenCheck, Route, Bike } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Config par trameKey :
 *  - livretPath   : chemin vers le livre interactif
 *  - chapters     : liste [{ id, n, title }] — doit MATCHER le CHAPTERS du livret
 *  - phaseToChapter : dict phaseId → chapterId pour l'auto-mapping
 */
const LIVRET_CONFIG = {
  plateau: {
    livretPath: '/livret-plateau',
    label: 'Livret Plateau A2',
    color: '#e11d48',
    Icon: Bike,
    chapters: [
      { id: 'cover', n: '',   title: 'Couverture' },
      { id: 'ch01',  n: '01', title: 'Bienvenue sur le plateau' },
      { id: 'ch02',  n: '02', title: 'Moto · Équipement · Commandes' },
      { id: 'ch03',  n: '03', title: 'Ton parcours plateau' },
      { id: 'ch04',  n: '04', title: 'Manœuvres 1 à 3' },
      { id: 'ch05',  n: '05', title: 'Manœuvres 4 à 6' },
      { id: 'ch06',  n: '06', title: 'Ma progression' },
      { id: 'ch07',  n: '07', title: 'Prêt pour l\'examen ?' },
    ],
    // Auto-mapping phase trame → chapitre livret
    // P1 (découverte) → ch02 (moto/commandes) : l'élève doit reconnaître son matériel
    // P2 (coordination) → ch04 (manœuvres 1-3) : cœur du plateau
    // P3 (maîtrise) → ch05 (manœuvres 4-6) : fin de plateau, examen proche
    phaseToChapter: {
      p1: 'ch02',
      p2: 'ch04',
      p3: 'ch05',
    },
  },
  circulation: {
    livretPath: '/livret-circulation',
    label: 'Livret Circulation A2',
    color: '#0ea5e9',
    Icon: Route,
    chapters: [
      { id: 'cover', n: '',   title: 'Couverture' },
      { id: 'ch01',  n: '01', title: 'Bienvenue en circulation' },
      { id: 'ch02',  n: '02', title: 'Prise en main avant départ' },
      { id: 'ch03',  n: '03', title: 'Regard · Allure · Position' },
      { id: 'ch04',  n: '04', title: 'Trajectoire de sécurité en virage' },
      { id: 'ch05',  n: '05', title: 'Cohabiter avec les autres usagers' },
      { id: 'ch06',  n: '06', title: 'Vérifications sécurité' },
      { id: 'ch07',  n: '07', title: 'Prêt pour l\'examen ?' },
    ],
    phaseToChapter: {
      // Trame Circulation historique : phases inconnues → mapping conservateur
      // sur le chapitre le plus contextuel.
    },
  },
};

function buildLivretLink(livretPath, chapterId) {
  if (typeof window === 'undefined') return '';
  const url = new URL(livretPath, window.location.origin);
  if (chapterId) url.searchParams.set('chapter', chapterId);
  return url.toString();
}

/**
 * @param {string} trameKey - 'plateau' | 'circulation' | '125' | 'passerelle'
 * @param {string} phaseId - id de la phase courante (utilisée pour l'auto-mapping)
 * @param {string} phaseCode - code lisible (P1/P2/P3) pour toast
 */
export default function TrameLivretQR({ trameKey, phaseId, phaseCode }) {
  const config = LIVRET_CONFIG[trameKey];
  const [manualChapterId, setManualChapterId] = useState(null);
  const [copied, setCopied] = useState(false);

  // Chapitre proposé : override manuel > auto-mapping > premier chapitre non-cover
  const suggestedChapterId = config?.phaseToChapter?.[phaseId];
  const activeChapterId =
    manualChapterId
    || suggestedChapterId
    || config?.chapters?.[1]?.id
    || 'ch01';

  const activeChapter = useMemo(
    () => config?.chapters?.find(c => c.id === activeChapterId),
    [config, activeChapterId],
  );

  const link = useMemo(
    () => (config ? buildLivretLink(config.livretPath, activeChapterId) : ''),
    [config, activeChapterId],
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(
        `Lien livret copié — chapitre ${activeChapter?.n || activeChapter?.title || activeChapterId}.`,
      );
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Long-press sur le QR pour partager.');
    }
  };

  // Si trameKey ne correspond à aucun livret publié (ex: 125, passerelle
  // sans livret interactif ciblé chapitre), on ne rend rien pour rester
  // silencieux plutôt que d'afficher un composant vide.
  if (!config) return null;

  const { livretPath, label, color, Icon, chapters } = config;
  const isAutoChoice = !manualChapterId && suggestedChapterId;

  return (
    <section
      className="rounded-2xl border overflow-hidden print:break-inside-avoid mt-4"
      style={{
        background: `linear-gradient(135deg, ${color}12 0%, transparent 70%)`,
        borderColor: `${color}55`,
      }}
      data-testid={`trame-livret-qr-${trameKey}-${phaseId}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
            style={{ color, borderColor: `${color}55`, background: `${color}15` }}
          >
            <BookOpenCheck className="w-3 h-3" /> QR livret · chapitre ciblé
          </span>
          {isAutoChoice && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              title={`Chapitre suggéré pour la phase ${phaseCode || phaseId}`}
            >
              Auto · {phaseCode || phaseId} → {activeChapter?.n || activeChapterId}
            </span>
          )}
        </div>

        <h3
          className="text-white font-black text-lg sm:text-xl leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Ouvrir le livret sur ce chapitre
        </h3>
        <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
          Ton élève scanne, et le <strong className="text-white">{label}</strong> s'ouvre directement au chapitre choisi — pratique pour cocher / documenter juste après la manœuvre.
        </p>

        {/* Sélecteur chapitre — permet l'override manuel */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label
            className="inline-flex items-center gap-2 h-9 px-2.5 rounded-lg bg-black/40 border border-white/10 text-[11px] font-black uppercase tracking-widest text-white/70"
            htmlFor={`livret-chapter-select-${trameKey}-${phaseId}`}
          >
            <Icon className="w-3.5 h-3.5" style={{ color }} />
            Chapitre :
          </label>
          <select
            id={`livret-chapter-select-${trameKey}-${phaseId}`}
            value={activeChapterId}
            onChange={(e) => setManualChapterId(e.target.value)}
            className="h-9 rounded-lg bg-white/[0.06] border border-white/15 text-white text-[12px] font-semibold px-2.5 focus:outline-none focus:border-white/40 hover:bg-white/[0.10] transition"
            data-testid={`livret-qr-chapter-select-${trameKey}`}
          >
            {chapters.map(c => (
              <option key={c.id} value={c.id} className="bg-neutral-900">
                {c.n ? `${c.n} · ${c.title}` : c.title}
              </option>
            ))}
          </select>
          {manualChapterId && suggestedChapterId && manualChapterId !== suggestedChapterId && (
            <button
              type="button"
              onClick={() => setManualChapterId(null)}
              className="h-9 px-2.5 rounded-lg bg-transparent border border-white/15 text-white/65 hover:text-white hover:bg-white/[0.05] text-[10.5px] font-black uppercase tracking-widest transition"
              data-testid={`livret-qr-reset-auto-${trameKey}`}
            >
              Reset auto
            </button>
          )}
        </div>

        <div className="mt-5 flex flex-col md:flex-row items-start gap-5 md:gap-6">
          {/* QR */}
          <div
            className="bg-white p-2.5 rounded-xl flex-shrink-0 mx-auto md:mx-0"
            style={{ boxShadow: `0 0 0 2px ${color}66, 0 12px 30px ${color}22` }}
          >
            <QRCodeSVG
              value={link}
              size={148}
              level="M"
              bgColor="#ffffff"
              fgColor="#0a0a0b"
              data-testid={`livret-qr-svg-${trameKey}-${phaseId}`}
            />
          </div>

          {/* Détails et actions */}
          <div className="flex-1 min-w-0 w-full">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">
              Chapitre pointé
            </div>
            <div className="text-white text-[15px] font-black leading-tight" data-testid={`livret-qr-chapter-label-${trameKey}`}>
              {activeChapter?.n ? `${activeChapter.n} · ${activeChapter.title}` : activeChapter?.title || '—'}
            </div>

            <div className="mt-3 rounded-lg bg-black/40 border border-white/10 px-3 py-2 font-mono text-[11px] text-white/75 break-all">
              {link || livretPath}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-white text-[11px] font-black uppercase tracking-widest transition"
                style={{ background: color }}
                data-testid={`livret-qr-copy-${trameKey}`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copié !' : 'Copier le lien'}
              </button>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[11px] font-black uppercase tracking-widest transition"
                data-testid={`livret-qr-open-${trameKey}`}
              >
                Ouvrir le livret
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
